// import dynamic from "next/dynamic";
// const Lottie = dynamic(() => import("lottie-react"), { ssr: false });
import Lottie from "lottie-react";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";

import { useStoreContext } from "../contexts/StoreDataContext";
import { toCdnUrl } from "../lib/cdn";
import { resolveImage } from "../lib/media";
import { truncateTo2Decimals } from "../services/frontend/pricing.service";
import { checkProductOrActiveVariantHasLowStock } from "../services/helper";
import useStore from "../store/search-store";
import Picture from "./Picture";
import * as animationData from "./shimmer.json";

// Browser-compatible path parsing
const parseFileName = (url) => {
  if (!url) return "";
  const parts = url.split("/");
  const filename = parts[parts.length - 1].split("?")[0];
  return {
    name: filename.substring(0, filename.lastIndexOf(".")),
    ext: filename.substring(filename.lastIndexOf(".")),
  };
};

const StoreItem = ({
  usedForCollectionhome,
  product,
  cohort,
  toggleDrawer,
  search,
  customHeightWidth,
}) => {
  const context = useStoreContext();
  const lowStockThreshold = context?.storeData?.storePrefs?.lowStockThreshold;
  const showLowStockAlert = checkProductOrActiveVariantHasLowStock({ product, lowStockThreshold });

  const [imageLoaded, setImageLoaded] = useState(false);
  const [fallbackSet, setFallbackSet] = useState(false);
  const [imageName, setImageName] = useState(null);
  const { setShouldSearchReopen } = useStore((state) => ({
    setShouldSearchReopen: state.setShouldSearchReopen,
  }));
  const router = useRouter();

  const getProductPrice = (price, mrp = "") => {
    if (!price) return;
    if (mrp) {
      const truncatedMrp = truncateTo2Decimals(mrp);

      let strikeOutMRP = (
        <span className="text-sm font-light line-through opacity-60">{`${String.fromCharCode(0x20b9)}${truncatedMrp}`}</span>
      );
      return (
        <>
          {`${String.fromCharCode(0x20b9)}${price}`} {strikeOutMRP}
        </>
      );
    } else {
      return `${String.fromCharCode(0x20b9)}${product.price}`;
    }
  };

  // media pipeline: compressed variants when a ready V2 entry exists (flag-gated), else today's URL
  const resolved = resolveImage(
    product?.productPhotos?.[0],
    product?.productPhotosV2,
    usedForCollectionhome ? "gridWide" : "grid",
  );

  const getImageUrl = () => {
    if (!imageName) return "";
    let imageurl = "";
    if (product.productPhotos && product.productPhotos[0]) {
      if (product.productPhotos[0].includes("dm2buy")) {
        imageurl = `https://dm2buy-resize-dynamic-cebdcaefgydgh6hu.z02.azurefd.net/dm2buy/${imageName}.jpg?width=400&height=400`;
      } else {
        imageurl = toCdnUrl(product.productPhotos[0]);
      }
    }
    return imageurl;
  };

  useEffect(() => {
    const urlPath =
      product.productPhotos && product.productPhotos[0]
        ? // eslint-disable-next-line no-undef
          new URL(product.productPhotos[0]).pathname
        : "";

    const extractedImageName = parseFileName(urlPath).name;
    setImageName(extractedImageName);
  }, [product.productPhotos]);

  const checkStock = () => {
    let stockAvailable = false;
    if (product.variantOptions && product.variantOptions.length) {
      product.variantOptions.forEach((variant) => {
        if (variant?.availableStock > 0) {
          stockAvailable = true;
        }
      });
    } else {
      return product.availableStock;
    }
    return stockAvailable;
  };

  // Calculate dynamic widths based on collection home usage
  const imageWidthClass = usedForCollectionhome
    ? "w-[calc((var(--max-width)/1.5)-50px)] h-[calc((var(--max-width)/1.5)-50px)]"
    : "w-[calc((100vw/2)-33px)] h-[calc((100vw/2)-33px)] md:w-[calc((520px/2)-33px)] md:h-[calc((520px/2)-33px)]";

  const itemWidthClass = usedForCollectionhome
    ? "max-w-[calc((var(--max-width)/1.5)-50px)] mb-3"
    : "max-w-[calc((100vw/2)-33px)] mb-8 md:max-w-[calc((520px/2)-33px)]";

  if (!product) return "";
  return (
    <div
      href={`/product/${product.id}`}
      className={`flex flex-col items-start justify-start ${itemWidthClass}`}
      onClick={() => {
        if (search) {
          setShouldSearchReopen(router.asPath);
        } else {
          setShouldSearchReopen(null);
        }
        router.push(`/product/${product.id}`);
        if (toggleDrawer) {
          toggleDrawer();
        }
      }}
    >
      <div>
        <div>
          {product && product?.productPhotos[0] && (
            <div className="relative rounded-lg">
              {!checkStock(product) && (
                <div className="absolute z-10 flex h-[98%] w-full cursor-pointer items-center justify-center rounded-lg text-2xl font-bold text-[#ff3333]">
                  <p className="m-0 p-0 tracking-[3px]">SOLD</p>
                </div>
              )}

              {showLowStockAlert && (
                <div className="absolute left-1.5 top-1.5 z-10 flex rounded-md bg-primary text-xs text-fontPrimary">
                  <p className="p-1.5">Only few left!</p>
                </div>
              )}

              {showLowStockAlert && (
                <div className="absolute left-1.5 top-1.5 z-10 flex rounded-md bg-primary text-xs text-fontPrimary">
                  <p className="p-1.5">Only few left!</p>
                </div>
              )}

              {!imageLoaded && (
                <div
                  className={`rounded-lg ${customHeightWidth ? customHeightWidth : imageWidthClass}`}
                >
                  <Lottie
                    className={"rounded-lg border " + (customHeightWidth && "h-full")}
                    animationData={animationData.default || animationData}
                    loop={true}
                    autoplay={true}
                    rendererSettings={{
                      preserveAspectRatio: "xMidYMid slice",
                    }}
                  />
                </div>
              )}

              {resolved.isV2 ? (
                <Picture
                  resolved={resolved}
                  alt={product.name}
                  style={
                    !imageLoaded
                      ? {
                          visibility: "hidden",
                          height: "0px !important",
                          width: "0px !important",
                          zIndex: 1,
                          position: "absolute",
                        }
                      : {
                          cursor: "pointer",
                        }
                  }
                  imgClassName={`rounded-lg object-cover ${customHeightWidth ? customHeightWidth : imageWidthClass} max-w-[none]`}
                  onLoad={() => {
                    setImageLoaded(true);
                  }}
                />
              ) : (
                <LazyLoadImage
                  style={
                    !imageLoaded
                      ? {
                          visibility: "hidden",
                          height: "0px !important",
                          width: "0px !important",
                          zIndex: 1,
                          position: "absolute",
                        }
                      : {
                          cursor: "pointer",
                        }
                  }
                  onLoad={() => {
                    setImageLoaded(true);
                  }}
                  onError={(e) => {
                    if (!fallbackSet) {
                      // Handle image load error by setting a fallback URL
                      e.target.src = `${product?.productPhotos[0]}`;
                      setFallbackSet(true);
                    }
                  }}
                  className={`rounded-lg object-cover ${customHeightWidth ? customHeightWidth : imageWidthClass} max-w-[none]`}
                  src={getImageUrl(imageName, cohort)}
                  unoptimized="true"
                  objectfit="cover"
                  alt={product.name}
                  effect={customHeightWidth ? "blur" : undefined}
                />
              )}
            </div>
          )}
        </div>

        <div className="flex w-full flex-col py-1">
          <div className="mb-1 cursor-pointer text-wrap text-base font-normal text-fontBackground opacity-70">
            {product && <div className="line-clamp-1">{product.name}</div>}
          </div>
          {/* <div className="mb-1 cursor-pointer text-wrap text-base font-normal text-fontBackground opacity-70">
            {product && (
              <LinesEllipsis
                text={
                  usedForCollectionhome
                    ? product.name.length > 20
                      ? `${product.name.substring(0, 20)}...`
                      : product.name
                    : product.name
                }
                maxLine="2"
                ellipsis="..."
                trimRight
                basedOn="letters"
              />
            )}
          </div> */}

          <div className="cursor-pointer text-base font-bold text-fontBackground">
            {product && getProductPrice(product.price, product.mrp)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreItem;
